import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { Html5Qrcode } from 'html5-qrcode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDoorOpen, faDoorClosed } from '@fortawesome/free-solid-svg-icons';
import "./QrCodeScanner.css";

const QrCodeScanner = () => {
    const [isActive, setIsActive] = useState(false);
    const [result, setResult] = useState(null);
    const [userName, setUserName] = useState('');
    const [registeredTime, setRegisteredTime] = useState('');
    const [message, setMessage] = useState(null);
    const [action, setAction] = useState(1);  // 1 for check-in, 0 for check-out
    const [isSoundEnabled, setIsSoundEnabled] = useState(true);  // Track if sound is enabled
    const qrScannerRef = useRef(null);
    const html5QrCodeRef = useRef(null);
    const timeoutRef = useRef(null);
    const messageTimeoutRef = useRef(null);
    const audioRef = useRef(null);  // Reference to the preloaded audio element

    const location = useLocation();
    const locationID = new URLSearchParams(location.search).get('locationID');

    useEffect(() => {
        if (isActive && qrScannerRef.current) {
            html5QrCodeRef.current = new Html5Qrcode(qrScannerRef.current.id);
            const config = { fps: 10, qrbox: { width: 320, height: 320 } };

            html5QrCodeRef.current.start(
                { facingMode: "environment" },
                config,
                (decodedText, decodedResult) => {
                    console.log(`Code matched = ${decodedText}`, decodedResult);
                    setResult(decodedText);
                    sendToServer(decodedText); // Send the result to the server for decryption
                    stopScanner(); // Stop after a successful scan
                },
                (error) => {
                    console.error('QR Code scan error:', error);
                }
            ).catch((error) => {
                console.error('Failed to start the QR Code scanner.', error);
            });

            timeoutRef.current = setTimeout(() => {
                stopScanner();
            }, 10000);
        }

        return () => {
            stopScanner();
        };
    }, [isActive]);

    // Preload the audio on component mount
    useEffect(() => {
        audioRef.current = new Audio(`${process.env.REACT_APP_SRV}/uploads/notifikacija.mp3`);
        audioRef.current.preload = 'auto';
    }, []);

    const startScanner = (scanAction) => {
        setAction(scanAction);  // Set action: 1 for check-in, 0 for check-out
        setIsActive(true);
    };

    const stopScanner = () => {
        if (html5QrCodeRef.current) {
            html5QrCodeRef.current.stop().then(() => {
                html5QrCodeRef.current = null;
                setIsActive(false);
                clearTimeout(timeoutRef.current);
            }).catch((error) => {
                console.error('Failed to stop the QR Code scanner.', error);
            });
        }
    };

    const sendToServer = async (decodedText) => {
        console.log('Sending to server:', decodedText);

        const requestBody = {
            encryptedData: decodedText,
            action: action,  // Use the action state for check-in (1) or check-out (0)
            datetime: new Date().toISOString() 
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_SRV}/attendance/scan`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'lid': locationID,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error('Failed to send data to server');
            }

            const responseData = await response.json();
            console.log('Response from server:', responseData);

            // Parse response to display the user's name, message, and formatted time
            setUserName(responseData.person);  // Expecting the "person" field from the server
            const time = new Date(responseData.registeredTime);
            const formattedTime = `${time.getHours()}:${time.getMinutes().toString().padStart(2, '0')}`;
            setRegisteredTime(formattedTime);
            setMessage(responseData.message);  // Message comes from the server, either "Welcome" or "Bye"

            // Play a beep sound if sound is enabled
            if (isSoundEnabled && audioRef.current) {
                audioRef.current.play().catch((error) => {
                    console.error('Audio play failed:', error);
                });
            }

            // Clear message after 3 seconds
            clearTimeout(messageTimeoutRef.current);
            messageTimeoutRef.current = setTimeout(() => {
                setMessage(null);
                setUserName('');
                setRegisteredTime('');
            }, 3000);

        } catch (error) {
            console.error('Error sending data to server:', error);
        }
    };

    return (
        <div className="container">
            {!isActive && (
                <div className="button-container">
                    <button onClick={() => startScanner(1)} className="round-button check-in">
                        <FontAwesomeIcon icon={faDoorOpen} size="3x" />
                    </button>
                    <div className="spacer"></div>
                    <button onClick={() => startScanner(0)} className="round-button check-out">
                        <FontAwesomeIcon icon={faDoorClosed} size="3x" />
                    </button>
                </div>
            )}

            {isActive && (
                <div className="camera-view">
                    <div ref={qrScannerRef} id="qr-scanner" style={{ width: '512px', transform: 'scaleX(-100%)' }}></div>
                </div>
            )}

            {message && (
                <div className={`message-box ${message === 'Welcome' ? 'green' : 'red'}`} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '10px', textAlign: 'center', fontSize: '2rem' }}>
                    {message} {userName} {registeredTime && `at ${registeredTime}`}
                </div>
            )}
        </div>
    );
};

export default QrCodeScanner;
